import { datadogLogs } from '@datadog/browser-logs';

import { useContext, useEffect, useRef } from 'react';
import { mutate } from 'swr';

import { eventBus } from '@/data/eventBus.tsx';
import {
  OAUTH_SESSION_EXPIRED,
  SUPABASE_SESSION_EXPIRED,
  SUPABASE_USER_FORBIDDEN,
} from '@/data/eventConstants.ts';
import {
  oauthProviderSpecificManager,
  supabase,
} from '@/utils/backend-auth.tsx';

import { backendAuthType, mixedLoginMethods } from '../../utils/constants.tsx';
import { useStoredOrganizationId } from '../useStoredOrganizationId/index.ts';
import { useSSOEmailVerificationStatus, useUser } from '../useSwr.tsx';
import { AuthContext } from './AuthContext.tsx';

export type AuthSession = {
  user: {
    id: string;
    email: string;
  };
};

export type Auth = ReturnType<typeof useAuth>;

export function useAuth() {
  const needsAuth =
    backendAuthType === 'supabase' ||
    backendAuthType === 'oauth' ||
    backendAuthType === 'mixed';

  const [session] = useContext(AuthContext);
  return {
    authed: !!session || !needsAuth,
    loading: session === undefined,
    session,
    needsAuth,
  };
}

// this is a little hacky, but it works for now
function findSupabaseKeys() {
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && /^sb-.*-auth-token$/.test(key)) {
      keys.push(key);
    }
  }
  return keys;
}

// This exists to handle when supabase session is null from their sdk.
// This is a signal that the user's session is potentially
// invalid. This is specifically for cloud supabase auth, because
// supabase.auth.onAuthStateChange doesn't fire a signout event when the session
// is invalid due to session timeout.
export function useMonitorAuth() {
  const [session, setSession] = useContext(AuthContext);
  const { orgId: organizationId } = useStoredOrganizationId();

  const hasClearedSession = useRef(false);

  useEffect(() => {
    if (session) {
      hasClearedSession.current = false;
    }
  }, [session]);

  useEffect(() => {
    const handleSessionExpired = () => {
      // Our auth state is stored in this context, setting it to null
      // shows the login screen.
      setSession(null);
      // We can't just call supabase.auth.signOut() because the session
      // is invalid and will not work. So we need to manually remove the
      // localstorage auth token.
      const supabaseAuthKeys = findSupabaseKeys();
      supabaseAuthKeys.forEach((key) => {
        localStorage.removeItem(key);
      });

      // clear fetch cache
      mutate(
        () => true, // all cache keys
        undefined, // update cache data to `undefined`
        { revalidate: false } // do not revalidate
      );
    };

    const unsubscribeExpired = eventBus.on<number>(
      SUPABASE_SESSION_EXPIRED,
      () => {
        if (hasClearedSession.current) return;
        hasClearedSession.current = true;
        supabase?.auth.getSession().then(({ data: { session: sbSession } }) => {
          if (sbSession == null) {
            datadogLogs?.logger.error(
              `Session expired for user ${session?.user?.id} - email ${session?.user?.email} in organization ${organizationId}`
            );
            handleSessionExpired();
          }
        });
      }
    );

    const unsubscribeForbidden = eventBus.on<number>(
      SUPABASE_USER_FORBIDDEN,
      () => {
        datadogLogs?.logger.error(
          `Supabase user query returned forbidden for user ${session?.user?.id} - email ${session?.user?.email} in organization ${organizationId}`
        );
        handleSessionExpired();
      }
    );

    return () => {
      unsubscribeExpired();
      unsubscribeForbidden();
    };
  }, []);
}

// This exists to handle when an oauth session has expired.
// This is specifically for self-hosted session-based oauth, because
// the oauth provider doesn't send a signal to the client when the session
// has expired - we just get 401s.
export function useMonitorOAuthSessioned() {
  const [session, _] = useContext(AuthContext);

  const hasClearedSession = useRef(false);
  const numEvents = useRef(0);

  useEffect(() => {
    if (session) {
      hasClearedSession.current = false;
    }
  }, [session]);

  useEffect(() => {
    const handleSessionExpired = () => {
      numEvents.current = 0;
      // clear fetch cache
      mutate(
        () => true, // all cache keys
        undefined, // update cache data to `undefined`
        { revalidate: false } // do not revalidate
      );

      oauthProviderSpecificManager?.signOut();
    };

    const unsubscribeExpired = eventBus.on<number>(
      OAUTH_SESSION_EXPIRED,
      () => {
        numEvents.current += 1;
        if (hasClearedSession.current) return;
        if (numEvents.current < 5) return;
        hasClearedSession.current = true;
        handleSessionExpired();
      }
    );

    return () => {
      if (mixedLoginMethods.customOidc) {
        unsubscribeExpired();
      }
    };
  }, []);
}

export function useUserSSOInfo() {
  const [session] = useContext(AuthContext);
  const { data: user } = useUser({
    skip: !session,
    revalidateOnFocus: false,
  });
  const ssoIdentities =
    user?.data?.user?.identities?.filter((i) => i.provider.includes('sso:')) ??
    [];

  const isSSOLogin = ssoIdentities?.length > 0;
  const ssoProviderId = ssoIdentities[0]?.provider.split(':')[1];

  const {
    data: ssoEmailVerificationStatus,
    isLoading: ssoEmailVerificationStatusLoading,
    error: ssoEmailVerificationStatusError,
  } = useSSOEmailVerificationStatus(
    session?.user?.email && ssoProviderId
      ? {
          email: session?.user?.email,
          saml_provider_id: ssoProviderId,
        }
      : undefined
  );
  return {
    isSSOEmailVerified: Boolean(ssoEmailVerificationStatus?.email_confirmed_at),
    isSSOLogin: isSSOLogin,
    ssoProviderId,
    isLoading: ssoEmailVerificationStatusLoading,
    error: ssoEmailVerificationStatusError,
  };
}
